import { fromEvent as observableFromEvent, Subject } from 'rxjs';

import { bufferToggle, debounceTime, distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core'
import { FormControl } from '@angular/forms'

import chaining from '../../../core/helpers/chaining';
import { QuantityChangedEvent } from '../../models/quantity-changed-event';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { FlyToCartService } from '../../services/fly-to-cart.service';

@Component({
  selector: 'app-quantity-picker-animated',
  templateUrl: './template.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('addButtonState', [
      state('default', style({
        visibility: 'visible',
        opacity: 1,
      })),
      state('active', style({
        visibility: 'hidden',
        opacity: 0,
      })),
      transition('default <=> active', animate('300ms linear'))
    ]),
    trigger('pickerState', [
      state('default', style({
        visibility: 'hidden',
        opacity: 0
      })),
      state('active', style({
        visibility: 'visible',
        opacity: 1
      })),
      transition('default <=> active', animate('300ms linear'))
    ])
  ],
  styleUrls: ['./styles.scss']
})
export class QuantityPickerAnimatedComponent implements OnChanges, AfterViewInit, OnDestroy {
  onDestroyed: Subject<void> = new Subject<void>()
  onChange = new Subject<number>()
  debouncer = new Subject<QuantityChangedEvent>()
  control = new FormControl()
  focused = false
  state = 'default'
  animationPossible = false

  @ViewChild('input') inputElement: ElementRef
  @ViewChild('wrapper') wrapperElement: ElementRef

  @Input() itemId: string
  @Input() source: string
  @Input() value: number
  @Input() minQuantity: number = 0
  @Input() stepping = 1
  @Input() deferToBlur = false
  @Input() zeroAllowed = true
  @Input() addButtonText: string
  @Input() quantityEditPermission = false

  @Input() alignment = 'flex-end'
  @Input() mode = 'regular'

  @Output() quantityChanged = new EventEmitter<QuantityChangedEvent>()

  constructor(private detectorRef: ChangeDetectorRef,
              private flyToCartSvc: FlyToCartService) {
    this.debouncer
      .pipe(
        debounceTime(500)
      )
      .subscribe(val => this.quantityChanged.emit(val))
  }

  ngAfterViewInit() {
    let input = this.inputElement.nativeElement

    let inputVal$ = this.control.valueChanges.pipe(
      distinctUntilChanged(null, value => value),
      map(value => this.isInteger ? value : this.minQuantity))

    let inputFocus$ = observableFromEvent(input, 'focus').pipe(
      takeUntil(this.onDestroyed))
    let inputBlur$ = observableFromEvent(input, 'blur').pipe(
      takeUntil(this.onDestroyed))

    inputFocus$.subscribe(() => {
      this.focused = true
      this.detectorRef.detectChanges()
    })
    inputBlur$.subscribe(() => {
      this.focused = false
    })

    this.onChange.subscribe(
      (val) => this.control.setValue(val, {emitEvent: false}))

    if(this.deferToBlur)
      inputVal$ = inputVal$.pipe(bufferToggle(inputFocus$, () => inputBlur$),
        filter(values => !!(values && values.length)),
        map((values: any[]) => values[values.length - 1]))

    inputBlur$.subscribe(() => {
      this.calculateMod()
    })

    this.onChange
      .subscribe(value => {
        value = this.quantityEditPermission
          ? value
          : value - (value % this.stepping)

        if(value < 0) {
          this.makeInactive()
          this.debouncer.next({
            quantity: 0,
            isEdited: false
          })
          this.control.setValue(0)
          this.makeInactive()
        }

        if(value === 0 && this.zeroAllowed) {
          this.debouncer.next({
            quantity: value,
            isEdited: this.quantityEditPermission && ((this.currentQuantity < this.minQuantity) || (this.currentQuantity % this.stepping !== 0))
          })
          this.makeInactive()
        }
        else if(this.currentQuantity <= this.minQuantity) {
          this.control.setValue(this.minQuantity)
          this.debouncer.next({
            quantity: this.minQuantity,
            isEdited: this.quantityEditPermission && ((this.currentQuantity < this.minQuantity) || (this.currentQuantity % this.stepping !== 0))
          })
        }

        if (value > 0) {
          if (this.animationPossible)
            this.flyToCartSvc.animateFlyToCart(this.itemId, this.source)
          this.debouncer.next({
            quantity: value,
            isEdited: this.quantityEditPermission && ((this.currentQuantity < this.minQuantity) || (this.currentQuantity % this.stepping !== 0))
          })
        }
        this.detectorRef.detectChanges()
      })
  }

  ngOnChanges(changes: SimpleChanges) {
    if(chaining(changes, 'value.currentValue') !== this.control.value) {
      this.control.setValue(isNaN(chaining(this, 'value')) ? chaining(this, 'value.quantity') : chaining(this, 'value'))
      if (chaining(changes, 'value.currentValue') === 0)
        this.makeInactive()
    }

    if(this.control.value)
      this.makeActive()
    this.detectorRef.detectChanges()
  }

  ngOnDestroy() {
    this.onDestroyed.next()
    this.onDestroyed.complete()
  }

  get isInteger(): boolean {
    return Number.isInteger(this.currentQuantity)
  }

  get currentQuantity(): number {
    return Number(this.control.value)
  }


  change(e, change) {
    if (change > 0)
      this.animationPossible = true
    else
      this.animationPossible = false
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    change = change * this.stepping

    this.detectorRef.detectChanges()
    this.onChange.next(Number(this.control.value) + change)
  }

  onSubmit(e) {
    this.inputElement.nativeElement.blur()
  }

  calculateMod() {
    let mod = this.control.value % this.stepping
    if((mod >= 0) && !this.quantityEditPermission)
      this.onChange.next(this.control.value - mod)
    else if(this.quantityEditPermission)
      this.onChange.next(this.control.value)
  }

  makeActive(e?: MouseEvent) {
    if(e) {
      e.preventDefault()
      e.stopPropagation()
    }

    if (this.state !== 'active')
      this.state = 'active'
    if(!this.control.value)
      this.change(e, 1)
  }

  makeInactive() {
    this.state = 'default'
    this.detectorRef.detectChanges()
  }

  prevent(e) {
    e.stopPropagation()
    e.preventDefault()
  }
}
